// CaseTimeline — Historial unificado de seguimientos y observaciones vinculadas.
// Sprint 5 · Paso 3 · muestra iconos por tipo de evidencia (texto/audio/foto)
// y abre modal con carga diferida al hacer click.
import React, { useState } from 'react';
import { FileText, ClipboardList, CheckCircle2, XCircle, UserPlus, Repeat, ArrowRight, Image as ImageIcon, Mic } from 'lucide-react';
import { nivelInfo } from '../../../lib/dashboardVocab';
import EvidenciaModal from './EvidenciaModal';

const ICON_TIPO = {
  asignacion:              UserPlus,
  cambio_estado:           ArrowRight,
  primera_accion:          CheckCircle2,
  seguimiento:             ClipboardList,
  cambio_carril:           Repeat,
  proxima_revision:        ClipboardList,
  cierre:                  XCircle,
  nota:                    FileText,
  vinculacion_estudiante:  UserPlus,
  obs_agregada:            ClipboardList,
};

const EVIDENCIA_ICON = { texto: FileText, foto: ImageIcon, audio: Mic };
const EVIDENCIA_LABEL = { texto: 'Nota/Relato', foto: 'Imagen', audio: 'Audio' };
const EVIDENCIA_COLOR = { texto: '#5346E6', foto: '#0F6C4B', audio: '#B97A0D' };

function fmt(iso) {
  try { return new Date(iso).toLocaleString('es-CL', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso || ''; }
}

export default function CaseTimeline({ observaciones = [], seguimientos = [] }) {
  const [modalEv, setModalEv] = useState(null);
  // Fusionar y ordenar por fecha desc (más reciente primero)
  const eventos = [
    ...seguimientos.map((s) => ({
      type: 'seguimiento',
      id: s.id,
      when: s.creado_en,
      title: labelTipo(s.tipo),
      description: s.descripcion,
      actor: s.actor_nombre,
      actorRol: s.actor_rol,
      Icon: ICON_TIPO[s.tipo] || FileText,
    })),
    ...observaciones.map((o) => ({
      type: 'observacion',
      id: `obs-${o.id}`,
      when: o.creado_en,
      title: 'Observación registrada',
      description: `${o.dominio_code} · ${o.conducta_code} · Intensidad ${o.intensidad || '—'} · Contexto ${o.contexto || '—'}`,
      nivel: o.nivel_interno,
      isSentinel: o.is_sentinel,
      evidencias: o.evidencias || [],
    })),
  ].sort((a, b) => new Date(b.when) - new Date(a.when));

  return (
    <section
      aria-label="Historial del caso"
      className="bg-white rounded-2xl border border-[#E5EAF5] p-5"
      data-testid="case-timeline"
    >
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-extrabold text-[#0B1B5E]">Historial y observaciones</h2>
          <p className="text-xs text-[#475472]">{eventos.length} evento{eventos.length !== 1 ? 's' : ''} en la línea de tiempo.</p>
        </div>
      </header>

      {eventos.length === 0 ? (
        <div className="py-6 text-sm text-[#475472] text-center" data-testid="timeline-empty">
          Sin registros.
        </div>
      ) : (
        <ol className="relative border-l-2 border-[#EEF0FE] ml-2 space-y-4" data-testid="timeline-list">
          {eventos.map((e) => {
            const Icon = e.Icon || (e.type === 'observacion' ? ClipboardList : FileText);
            const isObs = e.type === 'observacion';
            const nivelBadge = isObs && e.nivel ? nivelInfo(e.nivel) : null;
            return (
              <li key={e.id} className="pl-6 relative" data-testid={`timeline-item-${e.id}`}>
                <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center ${isObs ? 'bg-[#EEF0FE]' : 'bg-[#DFF6EC]'}`}>
                  <Icon className={`w-2.5 h-2.5 ${isObs ? 'text-[#5346E6]' : 'text-[#0F6C4B]'}`} />
                </span>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm font-bold text-[#0B1B5E]">
                    {e.title}
                    {nivelBadge && (
                      <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                            style={{ background: nivelBadge.bg, color: nivelBadge.fg }}>
                        <span className="w-1 h-1 rounded-full" style={{ background: nivelBadge.dot }} />
                        {nivelBadge.label}
                      </span>
                    )}
                    {e.isSentinel && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#FADBD8] text-[#8A1616]">
                        Centinela
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[#94A3B8]">{fmt(e.when)}</div>
                </div>
                {e.description && (
                  <p className="text-xs text-[#475472] mt-1 whitespace-pre-wrap">{e.description}</p>
                )}
                {isObs && e.evidencias && e.evidencias.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`evidencias-obs-${e.id}`}>
                    {e.evidencias.map((ev) => {
                      const EvIcon = EVIDENCIA_ICON[ev.tipo] || FileText;
                      const color = EVIDENCIA_COLOR[ev.tipo] || '#475472';
                      const label = EVIDENCIA_LABEL[ev.tipo] || ev.tipo;
                      const dur = ev.segundos ? ` · ${ev.segundos}s` : '';
                      return (
                        <button
                          key={ev.id}
                          type="button"
                          onClick={() => setModalEv({ id: ev.id, tipo: ev.tipo })}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border hover:shadow-sm transition"
                          style={{ borderColor: color + '40', background: color + '10', color }}
                          data-testid={`evidencia-btn-${ev.id}`}
                          aria-label={`Abrir evidencia ${label}${dur}`}
                        >
                          <EvIcon className="w-3 h-3" />
                          {label}{dur}
                        </button>
                      );
                    })}
                  </div>
                )}
                {e.actor && (
                  <p className="text-[11px] text-[#94A3B8] mt-1">
                    por {e.actor} {e.actorRol ? `(${e.actorRol})` : ''}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {modalEv && (
        <EvidenciaModal
          evidenciaId={modalEv.id}
          tipo={modalEv.tipo}
          onClose={() => setModalEv(null)}
        />
      )}
    </section>
  );
}

function labelTipo(t) {
  return {
    asignacion:              'Asignación de responsable',
    cambio_estado:           'Cambio de estado',
    primera_accion:          'Primera acción registrada',
    seguimiento:             'Seguimiento',
    cambio_carril:           'Cambio de carril',
    proxima_revision:        'Próxima revisión programada',
    cierre:                  'Cierre del caso',
    nota:                    'Nota interna',
    vinculacion_estudiante:  'Vinculación de estudiante',
    obs_agregada:            'Observación agregada',
  }[t] || t;
}
